/**
 * SelectionBoxDrawer Component
 * Drag-to-select tool for module selection
 *
 * Extracted from GeoTiffViewerEmbedded.tsx (lines 1787-2040)
 * PPT-style click-and-drag selection with rotation and snap-to-grid support
 */

import React, { useState, useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import { Polygon } from 'react-leaflet';
import { SolarModule } from '../modules';

export interface SelectionBoxDrawerProps {
  /** Whether the selection tool is active */
  isActive: boolean;

  /** Callback when a selection box is completed */
  onBoxDrawn: (box: [number, number][]) => void;

  /** Map of polygon IDs to their module arrays */
  modules: { [polygonId: number]: SolarModule[] };

  /** Currently selected polygon (null = all polygons) */
  selectedPolygon: number | null;
}

/**
 * SelectionBoxDrawer component
 *
 * Features:
 * - Click and drag to create selection box
 * - Automatically rotates box to match module grid rotation
 * - Snaps to nearby module edges (2-meter threshold)
 * - Respects spacebar for map panning
 * - Minimum size threshold to avoid accidental selections
 */
export function SelectionBoxDrawer({
  isActive,
  onBoxDrawn,
  modules,
  selectedPolygon,
}: SelectionBoxDrawerProps) {
  const map = useMap();
  const [isDragging, setIsDragging] = useState(false);
  const [startPoint, setStartPoint] = useState<[number, number] | null>(null);
  const [currentBox, setCurrentBox] = useState<[number, number][] | null>(null);
  const [boxRotation, setBoxRotation] = useState<number>(0);
  const isSpacePressedRef = useRef(false);

  // Track spacebar state to avoid interfering with panning
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        isSpacePressedRef.current = true;
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        isSpacePressedRef.current = false;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  /**
   * Snap a point to nearby module edges
   * Uses 2-meter threshold and transforms to module's rotated space
   */
  const snapToModuleEdges = (point: [number, number]): [number, number] => {
    if (selectedPolygon === null) return point;

    const polygonModules = modules[selectedPolygon] || [];
    if (polygonModules.length === 0) return point;

    const SNAP_THRESHOLD = 0.00002; // ~2 meters
    let snappedLat = point[0];
    let snappedLng = point[1];
    let minDistLat = Infinity;
    let minDistLng = Infinity;

    // Check each module's edges
    polygonModules.forEach((module) => {
      const rotation = module.rotation || 0;
      const radians = (rotation * Math.PI) / 180;
      const cos = Math.cos(radians);
      const sin = Math.sin(radians);

      // Get module dimensions
      const width = module.orientation === 'landscape' ? module.width : module.height;
      const height = module.orientation === 'landscape' ? module.height : module.width;
      const halfWidth = width / 2 / 111320; // Convert to degrees
      const halfHeight = height / 2 / 111320;

      // Calculate module's corner positions
      const centerLat = module.position[0];
      const centerLng = module.position[1];

      // Transform point to module's rotated space
      const deltaLat = point[0] - centerLat;
      const deltaLng = point[1] - centerLng;
      const rotatedLat = deltaLat * cos + deltaLng * sin;
      const rotatedLng = -deltaLat * sin + deltaLng * cos;

      // Module edges in rotated space
      const edges = {
        top: halfHeight,
        bottom: -halfHeight,
        right: halfWidth,
        left: -halfWidth,
      };

      // Check snap to each edge
      Object.entries(edges).forEach(([edgeName, edgeValue]) => {
        if (edgeName === 'top' || edgeName === 'bottom') {
          // Horizontal edge in rotated space
          const dist = Math.abs(rotatedLat - edgeValue);
          if (dist < SNAP_THRESHOLD && dist < minDistLat) {
            minDistLat = dist;
            // Transform back to world space
            const snappedRotLat = edgeValue;
            snappedLat = centerLat + (snappedRotLat * cos - rotatedLng * sin);
            snappedLng = centerLng + (snappedRotLat * sin + rotatedLng * cos);
          }
        } else {
          // Vertical edge in rotated space
          const dist = Math.abs(rotatedLng - edgeValue);
          if (dist < SNAP_THRESHOLD && dist < minDistLng) {
            minDistLng = dist;
            // Transform back to world space
            const snappedRotLng = edgeValue;
            snappedLat = centerLat + (rotatedLat * cos - snappedRotLng * sin);
            snappedLng = centerLng + (rotatedLat * sin + snappedRotLng * cos);
          }
        }
      });
    });

    return [snappedLat, snappedLng];
  };

  useEffect(() => {
    if (!isActive) return;

    const handleMouseDown = (e: any) => {
      // Don't interfere if spacebar is pressed (panning mode)
      if (isSpacePressedRef.current) {
        return;
      }

      // Only start selection if not clicking on a module, polygon, or other interactive element
      const target = e.originalEvent.target;
      const tagName = target.tagName.toLowerCase();

      // Don't start selection if clicking on interactive SVG elements
      // - path: polygons, modules, segments
      // - circle: CircleMarker handles (corners, edges, rotation)
      // - svg: SVG container (when using SVG renderer)
      if (tagName === 'path' || tagName === 'svg' || tagName === 'circle') {
        return;
      }

      // Don't interfere with draggable markers (e.g., the location pin).
      // Without this, the selection box steals slow drags from Leaflet's marker drag.
      if (target.classList && target.classList.contains('leaflet-marker-draggable')) {
        return;
      }

      // For Canvas renderer: check if Leaflet detected a layer under the click
      // The 'layer' property is set when clicking on an interactive vector layer
      if (e.layer && e.layer._path) {
        // Click was on an interactive layer (segment, polygon, module)
        return;
      }

      // Detect rotation from modules in selected polygon
      let rotation = 0;
      if (selectedPolygon !== null) {
        const polygonModules = modules[selectedPolygon] || [];
        if (polygonModules.length > 0) {
          // Use rotation from first module
          rotation = polygonModules[0].rotation || 0;
        }
      }

      // Start drag-select
      setIsDragging(true);
      setStartPoint([e.latlng.lat, e.latlng.lng]);
      setBoxRotation(rotation);
      map.dragging.disable();
    };

    const handleMouseMove = (e: any) => {
      if (!isDragging || !startPoint) return;
      let currentPoint: [number, number] = [e.latlng.lat, e.latlng.lng];

      // Snap current point to nearby module edges
      currentPoint = snapToModuleEdges(currentPoint);

      // Create rotated selection box
      const radians = (-boxRotation * Math.PI) / 180;
      const cos = Math.cos(radians);
      const sin = Math.sin(radians);

      // Use startPoint as center for rotation
      const centerLat = startPoint[0];
      const centerLng = startPoint[1];

      // Transform both points to rotated space
      const transformToRotated = (lat: number, lng: number): [number, number] => {
        const deltaLat = lat - centerLat;
        const deltaLng = lng - centerLng;
        return [deltaLat * cos - deltaLng * sin, deltaLat * sin + deltaLng * cos];
      };

      const transformFromRotated = (rotLat: number, rotLng: number): [number, number] => {
        return [
          centerLat + (rotLat * cos + rotLng * sin),
          centerLng + (-rotLat * sin + rotLng * cos),
        ];
      };

      const startRotated = transformToRotated(startPoint[0], startPoint[1]);
      const currentRotated = transformToRotated(currentPoint[0], currentPoint[1]);

      // Create axis-aligned box in rotated space
      const minRotLat = Math.min(startRotated[0], currentRotated[0]);
      const maxRotLat = Math.max(startRotated[0], currentRotated[0]);
      const minRotLng = Math.min(startRotated[1], currentRotated[1]);
      const maxRotLng = Math.max(startRotated[1], currentRotated[1]);

      // Transform corners back to world space
      const box: [number, number][] = [
        transformFromRotated(maxRotLat, minRotLng), // top-left
        transformFromRotated(maxRotLat, maxRotLng), // top-right
        transformFromRotated(minRotLat, maxRotLng), // bottom-right
        transformFromRotated(minRotLat, minRotLng), // bottom-left
        transformFromRotated(maxRotLat, minRotLng), // close
      ];

      setCurrentBox(box);
    };

    const handleMouseUp = (e: any) => {
      if (!isDragging || !startPoint || !currentBox) {
        setIsDragging(false);
        setStartPoint(null);
        setCurrentBox(null);
        // Don't call map.dragging.enable() - let SpacebarPanController manage it
        return;
      }

      // Check if box has meaningful size
      const endPoint: [number, number] = [e.latlng.lat, e.latlng.lng];
      const latDiff = Math.abs(startPoint[0] - endPoint[0]);
      const lngDiff = Math.abs(startPoint[1] - endPoint[1]);

      if (latDiff > 0.00001 && lngDiff > 0.00001) {
        onBoxDrawn(currentBox);
      }

      setIsDragging(false);
      setStartPoint(null);
      setCurrentBox(null);
      // Don't call map.dragging.enable() - let SpacebarPanController manage it
    };

    map.on('mousedown', handleMouseDown);
    map.on('mousemove', handleMouseMove);
    map.on('mouseup', handleMouseUp);

    return () => {
      map.off('mousedown', handleMouseDown);
      map.off('mousemove', handleMouseMove);
      map.off('mouseup', handleMouseUp);
      // Note: Don't enable dragging here - let SpacebarPanController manage it
    };
  }, [isActive, isDragging, startPoint, currentBox, map, onBoxDrawn]);

  // Render selection box
  if (!currentBox) return null;

  return (
    <Polygon
      positions={currentBox}
      pathOptions={{
        color: '#3b82f6',
        fillColor: '#3b82f6',
        fillOpacity: 0.1,
        weight: 2,
        dashArray: '5, 5',
      }}
      // @ts-ignore - react-leaflet v5 supports interactive but v2 types don't
      interactive={false}
    />
  );
}
